import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import Pagination from '../components/shared/Pagination';
import Text from '../components/shared/Text';

function ReturnOrdersIndex() {
    const [returns, setReturns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [pagination, setPagination] = useState({ 
        total_pages: 1, 
        total_count: 0, 
        current_page: 1,
        per_page: 25 
    });
    const [statusFilter, setStatusFilter] = useState('');

    useEffect(() => {
        loadReturns();
    }, [search, currentPage, statusFilter]);

    const loadReturns = async () => {
        try {
            setLoading(true);
            const params: any = { q: search, page: currentPage };
            if (statusFilter) params.status = statusFilter;

            const response = await api.getReturnOrders(params);
            if (response.success) {
                setReturns(response.data || []);
                if (response.meta) {
                    setPagination({
                        total_pages: response.meta.total_pages || 1,
                        total_count: response.meta.total_count || 0,
                        current_page: response.meta.current_page || 1,
                        per_page: response.meta.per_page || 25
                    });
                }
            } else {
                setError(response.message || 'Failed to load returns');
            }
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'approved': return 'bg-emerald-50 text-emerald-700 border-emerald-100';
            case 'pending': return 'bg-amber-50 text-amber-700 border-amber-100';
            case 'rejected': return 'bg-rose-50 text-rose-700 border-rose-100';
            case 'completed': return 'bg-blue-50 text-blue-700 border-blue-100';
            default: return 'bg-slate-50 text-slate-700 border-slate-100';
        }
    };

    if (loading && returns.length === 0) {
        return (
            <div className="mt-16 p-8 text-center">
                <Text variant="body" color="slate" weight="medium">Loading returns...</Text>
            </div>
        );
    }

    return (
        <div className="mt-16 pb-12">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
                <div>
                    <Text variant="h1" color="primary">Returns & Refunds</Text>
                    <Text variant="body" color="slate" className="mt-1">Manage product returns and credit notes ({pagination.total_count} found)</Text>
                </div>
            </div>

            <div className="mb-6 space-y-4">
                <div className="relative">
                    <svg className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <input
                        type="text"
                        placeholder="Search returns by number, invoice or customer..."
                        value={search}
                        onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
                        className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 outline-none text-sm transition-all shadow-sm shadow-slate-100"
                    />
                </div>

                <div className="flex flex-wrap gap-2">
                    <button
                        onClick={() => { setStatusFilter(''); setCurrentPage(1); }}
                        className={`px-4 py-2 rounded-xl text-xs font-bold uppercase tracking-wider transition-all border ${!statusFilter ? 'bg-indigo-600 text-white border-indigo-600 shadow-lg shadow-indigo-600/20' : 'bg-white text-slate-600 border-slate-200 hover:border-indigo-300 hover:text-indigo-600'}`}
                    >
                        All
                    </button>
                    <button
                        onClick={() => { setStatusFilter('pending'); setCurrentPage(1); }}
                        className={`px-4 py-2 rounded-xl text-xs font-bold uppercase tracking-wider transition-all border ${statusFilter === 'pending' ? 'bg-amber-500 text-white border-amber-500 shadow-lg shadow-amber-500/20' : 'bg-white text-slate-600 border-slate-200 hover:border-amber-300 hover:text-amber-600'}`}
                    >
                        Pending
                    </button>
                    <button
                        onClick={() => { setStatusFilter('approved'); setCurrentPage(1); }}
                        className={`px-4 py-2 rounded-xl text-xs font-bold uppercase tracking-wider transition-all border ${statusFilter === 'approved' ? 'bg-emerald-600 text-white border-emerald-600 shadow-lg shadow-emerald-600/20' : 'bg-white text-slate-600 border-slate-200 hover:border-emerald-300 hover:text-emerald-600'}`}
                    >
                        Approved
                    </button>
                    <button
                        onClick={() => { setStatusFilter('completed'); setCurrentPage(1); }}
                        className={`px-4 py-2 rounded-xl text-xs font-bold uppercase tracking-wider transition-all border ${statusFilter === 'completed' ? 'bg-blue-600 text-white border-blue-600 shadow-lg shadow-blue-600/20' : 'bg-white text-slate-600 border-slate-200 hover:border-blue-300 hover:text-blue-600'}`}
                    >
                        Completed
                    </button>
                </div>
            </div>

            {error && (
                <div className="bg-rose-50 border-l-4 border-rose-500 p-4 rounded-md mb-6 shadow-sm">
                    <Text variant="small" weight="medium" className="text-rose-700">{error}</Text>
                </div>
            )}

            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden mb-6">
                {returns.length > 0 ? (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-slate-50 border-b border-slate-200">
                                <tr>
                                    <th className="px-6 py-4 text-left"><Text variant="label" color="slate">Return #</Text></th>
                                    <th className="px-6 py-4 text-left"><Text variant="label" color="slate">Invoice</Text></th>
                                    <th className="px-6 py-4 text-left"><Text variant="label" color="slate">Customer</Text></th>
                                    <th className="px-6 py-4 text-left"><Text variant="label" color="slate">Date</Text></th>
                                    <th className="px-6 py-4 text-left"><Text variant="label" color="slate">Reason</Text></th>
                                    <th className="px-6 py-4 text-center"><Text variant="label" color="slate">Status</Text></th>
                                    <th className="px-6 py-4 text-right"><Text variant="label" color="slate">Refund</Text></th>
                                    <th className="px-6 py-4 text-right"><Text variant="label" color="slate">Actions</Text></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {returns.map((returnOrder: any) => (
                                    <tr key={returnOrder.id} className="hover:bg-slate-50/50 transition-colors">
                                        <td className="px-6 py-4">
                                            <Link to={`/returns/${returnOrder.id}`}>
                                                <Text variant="mono" weight="bold" className="text-indigo-600 hover:text-indigo-800">
                                                    {returnOrder.return_number}
                                                </Text>
                                            </Link>
                                        </td>
                                        <td className="px-6 py-4">
                                            <Link to={`/invoices/${returnOrder.invoice_id}`}>
                                                <Text variant="body" weight="medium" color="slate" className="hover:text-indigo-600">
                                                    {returnOrder.invoice_number}
                                                </Text>
                                            </Link>
                                        </td>
                                        <td className="px-6 py-4">
                                            <Text variant="body" weight="medium" color="primary">{returnOrder.customer_name}</Text>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex flex-col">
                                                <Text variant="body" color="primary">{new Date(returnOrder.return_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}</Text>
                                                <Text variant="small" color="slate" className="capitalize">{new Date(returnOrder.created_at || returnOrder.return_date).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}</Text>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-slate-600 capitalize">
                                            <Text variant="body" color="slate" className="truncate max-w-[150px] inline-block">{returnOrder.reason?.replace('_', ' ')}</Text>
                                        </td>
                                        <td className="px-6 py-4 text-center">
                                            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-bold border uppercase tracking-wider ${getStatusColor(returnOrder.status)}`}>
                                                {returnOrder.status}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <Text variant="mono" weight="bold" color="primary" className="uppercase tracking-tighter">
                                                ₹{returnOrder.refund_amount?.toLocaleString()}
                                            </Text>
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <Link
                                                to={`/returns/${returnOrder.id}`}
                                                className="inline-flex items-center gap-1.5"
                                            >
                                                <Text variant="label" weight="bold" className="text-indigo-600 hover:text-indigo-800">Details</Text>
                                                <svg className="w-3.5 h-3.5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 5l7 7-7 7"/></svg>
                                            </Link>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="py-20 text-center">
                        <div className="w-20 h-20 bg-slate-50 rounded-3xl flex items-center justify-center mx-auto mb-6">
                            <svg className="w-10 h-10 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                            </svg>
                        </div>
                        <Text variant="h3" color="primary" className="mb-2">No returns found</Text>
                        <Text variant="body" color="slate" className="max-w-sm mx-auto">Try adjusting your filters or search terms.</Text>
                    </div>
                )}
            </div>

            <Pagination
                currentPage={pagination.current_page}
                totalPages={pagination.total_pages}
                totalCount={pagination.total_count}
                perPage={pagination.per_page}
                onPageChange={handlePageChange}
            />
        </div>
    );
}

export default ReturnOrdersIndex;
